import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
    Pencil,
    Loader2,
    AlertCircle,
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Save,
    XCircle,
    Camera
} from 'lucide-react';
import './Profile.css';


const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;



const Profile = () => {
    const [profileData, setProfileData] = useState(null);
    const [loading,setLoading] = useState(true);
    const [error, setError] = useState('');

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // State for image handling
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const userId = localStorage.getItem('userId');


    const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

    useEffect(() => {
        const fetchProfileData = async () => {
            if (!userId) {
                setError("You are not logged in. Please log in to view your profile.");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const response = await axios.get(`${REACT_APP_API_BASE}/api/user/${userId}`);
                setProfileData(response.data);
            } catch (err) {
                setError(err.response?.data?.message || err.message || "An error occurred while fetching your profile.");
            } finally {
                setLoading(false);
            }
        };
        fetchProfileData();
    }, [userId]);

    // --- EVENT HANDLERS ---

    const handleAvatarClick = () => {
        // Only allow clicking the avatar if in edit mode
        if (isEditing) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleEditClick = () => {
        setFormData({ ...profileData });
        setIsEditing(true);
        setSubmitError('');
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setSubmitError('');
        // Clear any staged file changes
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // ✅ REVISED SAVE FUNCTION
    const handleSave = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!formData.firstName || !formData.lastName) {
            setSubmitError("First name and last name are both required.");
            return;
        }

        setIsSubmitting(true);
        let dataToUpdate = { ...formData };

        // Step 1: Upload the image if a new one is selected
        if (selectedFile) {
            try {
                const imageFormData = new FormData();
                imageFormData.append('avatar', selectedFile);
                const imageResponse = await axios.post(
                    `${REACT_APP_API_BASE}/api/user/${userId}/avatar`,
                    imageFormData
                );
                // Add the new avatar URL to our data object for the final update
                dataToUpdate.avatarUrl = imageResponse.data.avatarUrl;
            } catch (err) {
                setSubmitError("Failed to upload new avatar. Please try again.");
                setIsSubmitting(false);
                return; // Stop the save process if image upload fails
            }
        }

        // Step 2: Update the rest of the profile data
        try {
            const profileResponse = await axios.put(
                `${REACT_APP_API_BASE}/api/user/${userId}`,
                dataToUpdate
            );
            setProfileData(profileResponse.data);
            setIsEditing(false);
            // Clear staged files on successful save
            setSelectedFile(null);
            setPreviewUrl(null);
        } catch (err) {
            setSubmitError(err.response?.data?.message || "Failed to update profile information.");
        } finally {
            setIsSubmitting(false);
        }
    };


    // --- HELPER FUNCTIONS & RENDER LOGIC --- (No changes needed below this line)

    const formatDate = (dateString) => {
        if (!dateString) return 'Not Provided';
        try { return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } 
        catch { return 'Invalid Date'; }
    };
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try { return new Date(dateString).toISOString().split('T')[0]; } 
        catch { return ''; }
    };

    if (loading) return <div className="profile-status-container"><Loader2 className="animate-spin" size={48} /> <p>Loading Profile...</p></div>;
    if (error) return <div className="profile-status-container error-message"><AlertCircle size={48} /> <p>Could not load profile.</p> <span>{error}</span></div>;
    if (!profileData) return <div className="profile-status-container">No profile data available.</div>;

    const displayData = isEditing ? formData : profileData;
    const fullName = `${displayData.firstName || ''} ${displayData.lastName || ''}`.trim();

    // Determine which image URL to show: preview, existing data, or nothing
    const avatarSrc = previewUrl || (isEditing ? formData.avatarUrl : profileData.avatarUrl);

    return (
        <>

 <Helmet>
          <meta name="title" content="Home | Get Next Visa - AI Powered Student Visa Report" />
        <title> My Profile | Get Next Visa - AI Powered Student Visa Report</title>
        <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
      </Helmet>


        <div className="profile-page-container">
            <form className="profile-grid" onSubmit={handleSave}>
                <div className="profile-summary-card">
                    {/* ✅ REVISED AVATAR SECTION */}
                    <div
                        className={`profile-avatar-container ${isEditing ? 'editable' : ''}`}
                        onClick={handleAvatarClick}
                    >
                        <div className="profile-avatar-large">
                            {avatarSrc ? (
                                <img src={avatarSrc} alt="Profile Avatar" className="profile-avatar-image" />
                            ) : (
                                (fullName || 'U').charAt(0).toUpperCase()
                            )}
                        </div>
                        {isEditing && (
                            <div className="profile-avatar-overlay">
                                <Camera size={24} />
                            </div>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg"
                    />

                    <h1 className="profile-name-large">{fullName || 'User Profile'}</h1>
                    <p className="profile-email-large"><Mail size={16} /> {profileData.email}</p>

                    <div className="profile-completion">
                        {/* ... completion bar logic ... */}
                    </div>

                    {isEditing ? (
                        <div className="edit-mode-buttons">
                            <button type="submit" className="save-profile-btn" disabled={isSubmitting}>
                                {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type="button" className="cancel-profile-btn" onClick={handleCancelClick}>
                                <XCircle size={16} /> Cancel
                            </button>
                        </div>
                    ) : (
                        <button type="button" className="edit-profile-btn" onClick={handleEditClick}>
                            <Pencil size={16} /> Edit Profile
                        </button>
                    )}
                    {submitError && <p className="submit-error-message">{submitError}</p>}
                </div>

                {/* Right Column: Detailed Information */}
                <div className="profile-details-card">
                    {/* ... (this part is unchanged) ... */}
                    <div className="details-header"><h2>Personal Information</h2></div>
                    <div className="details-grid">
                        {isEditing ? (
                            <>
                                <div className="detail-item">
                                    <User className="detail-icon" />
                                    <div>
                                        <span className="detail-label">First Name</span>
                                        <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} className="profile-input" placeholder="e.g., John" />
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <div>
                                        <span className="detail-label">Last Name</span>
                                        <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} className="profile-input" placeholder="e.g., Doe" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="detail-item">
                                <User className="detail-icon" />
                                <div>
                                    <span className="detail-label">Full Name</span>
                                    <p className="detail-value">{fullName || 'Not Provided'}</p>
                                </div>
                            </div>
                        )}
                        <div className="detail-item">
                            <Phone className="detail-icon" />
                            <div>
                                <span className="detail-label">Phone Number</span>
                                {isEditing ? ( <input type="tel" name="phone" value={formData.phone || ''} onChange={handleInputChange} className="profile-input" placeholder="e.g., 555-123-4567" /> ) : ( <p className="detail-value">{profileData.phone || 'Not Provided'}</p> )}
                            </div>
                        </div>
                        <div className="detail-item">
                            <Calendar className="detail-icon" />
                            <div>
                                <span className="detail-label">Date of Birth</span>
                                {isEditing ? ( <input type="date" name="dob" value={formatDateForInput(formData.dob)} onChange={handleInputChange} className="profile-input" /> ) : ( <p className="detail-value">{formatDate(profileData.dob)}</p> )}
                            </div>
                        </div>
                        <div className="detail-item">
                            <MapPin className="detail-icon" />
                            <div>
                                <span className="detail-label">Location</span>
                                {isEditing ? ( <input type="text" name="location" value={formData.location || ''} onChange={handleInputChange} className="profile-input" placeholder="e.g., New York, NY" /> ) : ( <p className="detail-value">{profileData.location || 'Not Provided'}</p> )}
                            </div>
                        </div>
                    </div>
                    <div className="bio-section">
                        <h3 className="bio-title">About Me</h3>
                        {isEditing ? ( <textarea name="bio" value={formData.bio || ''} onChange={handleInputChange} className="profile-textarea" rows="5" placeholder="Tell us a little about yourself..."></textarea> ) : ( <p className="bio-text">{profileData.bio || 'No bio has been provided. Click "Edit Profile" to add one.'}</p> )}
                    </div>
                </div>
            </form>
        </div>
        </>
    );
};

export default Profile;